/**
 * 📊 Multi Campaign Dashboard
 * Dashboard für Multi-Campaign Übersicht und Fortschritts-Tracking
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { 
  Play, Pause, Square, Clock, 
  Mail, CheckCircle, XCircle, Layers,
  ChevronRight, RefreshCw,
  TrendingUp, Activity, Target
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const MultiCampaignDashboard = () => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [refreshInterval] = useState(5000); // 5 Sekunden

  // Multi-Campaigns laden
  const { 
    data: campaigns, 
    isLoading, 
    refetch: refetchCampaigns 
  } = useQuery(
    'multiCampaigns', 
    async () => {
      const response = await axios.get('/api/multi-campaigns');
      return response.data.data || [];
    },
    {
      refetchInterval: refreshInterval,
      refetchIntervalInBackground: true
    }
  );

  // Campaign Details laden
  const { 
    data: campaignDetails, 
    refetch: refetchDetails 
  } = useQuery(
    ['multiCampaignDetails', selectedCampaign?.campaign_id],
    async () => {
      if (!selectedCampaign) return null;
      const response = await axios.get(`/api/multi-campaigns/${selectedCampaign.campaign_id}`);
      return response.data.data;
    },
    {
      enabled: !!selectedCampaign,
      refetchInterval: selectedCampaign ? refreshInterval : false
    }
  );

  // Campaign starten Mutation
  const startCampaignMutation = useMutation(
    async (campaignId) => {
      const response = await axios.post(`/api/multi-campaigns/${campaignId}/start`);
      return response.data;
    },
    {
      onSuccess: (data, campaignId) => {
        toast.success('Multi-Campaign gestartet!');
        refetchCampaigns();
        if (selectedCampaign?.campaign_id === campaignId) {
          refetchDetails();
        }
      },
      onError: (error) => {
        toast.error('Fehler beim Starten der Campaign');
        console.error('Start campaign error:', error);
      }
    }
  );

  // Status-Badge Komponente
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      draft: { color: 'gray', icon: Clock, text: 'Entwurf' },
      pending: { color: 'yellow', icon: Clock, text: 'Wartend' },
      running: { color: 'blue', icon: Activity, text: 'Läuft' },
      paused: { color: 'orange', icon: Pause, text: 'Pausiert' },
      completed: { color: 'green', icon: CheckCircle, text: 'Abgeschlossen' },
      failed: { color: 'red', icon: XCircle, text: 'Fehlgeschlagen' },
      cancelled: { color: 'gray', icon: Square, text: 'Abgebrochen' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  // Fortschrittsbalken Komponente
  const ProgressBar = ({ current, total, className = "" }) => {
    const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
    
    return (
      <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  // Campaign-Karte Komponente
  const CampaignCard = ({ campaign }) => {
    const totalSlots = campaign.total_slots || campaign.slot_count || 0;
    const totalSent = campaign.total_sent || 0;
    const totalFailed = campaign.total_failed || 0;
    const totalRecipients = campaign.total_recipients || 0;
    const totalSegments = campaign.total_segments || 0;
    const runningSegments = campaign.running_segments || 0;
    const completedSegments = campaign.completed_segments || 0;
    const pendingSegments = campaign.pending_segments || 0;

    const successRate = totalSent + totalFailed > 0 
      ? Math.round((totalSent / (totalSent + totalFailed)) * 100) 
      : 0;

    const modeLabels = {
      sequential: 'Sequenziell',
      parallel: 'Parallel',
      segmented_parallel: 'Provider-Seg.',
      smtp_segmented_parallel: 'SMTP-Seg.',
      smtp_round_robin: 'Round Robin',
      burst_parallel: 'Burst'
    };

    const isActive = campaign.status === 'running' || campaign.status === 'pending';

    return (
      <div 
        className={`bg-white dark:bg-dark-secondary rounded-lg shadow border ${isActive ? 'border-blue-400 dark:border-blue-600 ring-1 ring-blue-200' : 'border-gray-200 dark:border-dark-copper-dark'} p-5 hover:shadow-md transition-shadow cursor-pointer`}
        onClick={() => setSelectedCampaign(campaign)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{campaign.name}</h3>
            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-dark-surface text-gray-600 dark:text-gray-300 font-mono">
              {modeLabels[campaign.execution_mode] || campaign.execution_mode}
            </span>
          </div>
          <StatusBadge status={campaign.status} />
        </div>

        {/* Segment-Leiste */}
        {totalSegments > 0 && (
          <div className="mb-3 flex items-center space-x-3 text-xs">
            <span className="text-gray-500 dark:text-gray-400">Segmente:</span>
            {runningSegments > 0 && (
              <span className="flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium">
                <Activity className="h-3 w-3 mr-1 animate-pulse" />
                {runningSegments} aktiv
              </span>
            )}
            {completedSegments > 0 && (
              <span className="flex items-center px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                <CheckCircle className="h-3 w-3 mr-1" />
                {completedSegments}
              </span>
            )}
            {pendingSegments > 0 && (
              <span className="flex items-center px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
                <Clock className="h-3 w-3 mr-1" />
                {pendingSegments}
              </span>
            )}
            <span className="text-gray-400 dark:text-gray-500">/ {totalSegments}</span>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-5 gap-3 mb-3">
          <div className="text-center bg-gray-50 dark:bg-dark-surface rounded-lg py-2">
            <div className="text-lg font-bold text-blue-600">{totalRecipients.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Empfänger</div>
          </div>
          <div className="text-center bg-gray-50 dark:bg-dark-surface rounded-lg py-2">
            <div className="text-lg font-bold text-green-600">{totalSent.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Gesendet</div>
          </div>
          <div className="text-center bg-gray-50 dark:bg-dark-surface rounded-lg py-2">
            <div className="text-lg font-bold text-red-600">{totalFailed.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Failed</div>
          </div>
          <div className="text-center bg-gray-50 dark:bg-dark-surface rounded-lg py-2">
            <div className="text-lg font-bold text-purple-600">{successRate}%</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rate</div>
          </div>
          <div className="text-center bg-gray-50 dark:bg-dark-surface rounded-lg py-2">
            <div className="text-lg font-bold text-orange-600">{totalSlots.toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Slots</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-1 mb-3">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>Fortschritt</span>
            <span>{totalRecipients > 0 ? Math.round(((totalSent + totalFailed) / totalRecipients) * 100) : 0}% ({(totalSent + totalFailed).toLocaleString()}/{totalRecipients.toLocaleString()})</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-dark-surface rounded-full h-2.5 overflow-hidden">
            <div className="h-full rounded-full flex">
              <div className="bg-green-500 h-full transition-all duration-500" style={{ width: `${totalRecipients > 0 ? (totalSent / totalRecipients) * 100 : 0}%` }} />
              <div className="bg-red-400 h-full transition-all duration-500" style={{ width: `${totalRecipients > 0 ? (totalFailed / totalRecipients) * 100 : 0}%` }} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-400 dark:text-gray-500">
            {campaign.created_at && new Date(campaign.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </div>
          
          {campaign.status === 'draft' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                startCampaignMutation.mutate(campaign.campaign_id);
              }}
              disabled={startCampaignMutation.isLoading}
              className="px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1 font-medium"
            >
              <Play className="h-3 w-3" />
              <span>Starten</span>
            </button>
          )}
        </div>
      </div>
    );
  };

  // Slot-Details Komponente
  const SlotDetails = ({ slot, slotNumber }) => {
    const sentCount = slot.sent_count || 0;
    const failedCount = slot.failed_count || 0;
    const recipientCount = slot.recipient_count || 0;
    const progress = recipientCount > 0 ? Math.round(((sentCount + failedCount) / recipientCount) * 100) : 0;

    return (
      <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-copper-dark p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-blue-600">{slotNumber}</span>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-200">{slot.name}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-300">{slot.description}</p>
            </div>
          </div>
          <StatusBadge status={slot.status} />
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-600">{sentCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-300">Gesendet</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-red-600">{failedCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-300">Fehler</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-600">{recipientCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-300">Gesamt</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-900 dark:text-gray-200">
            <span>Fortschritt:</span>
            <span>{progress}%</span>
          </div>
          <ProgressBar current={sentCount + failedCount} total={recipientCount} />
        </div>

        <div className="mt-3 text-xs text-gray-500 dark:text-gray-300 space-y-1">
          <div>Empfänger-Quelle: {slot.recipient_source === 'own_list' ? 'Eigene Liste' : 'Domain-Segmentierung'}</div>
          <div>Rate: {slot.emails_per_hour}/h</div>
          {slot.started_at && (
            <div>Gestartet: {new Date(slot.started_at).toLocaleString()}</div>
          )}
          {slot.completed_at && (
            <div>Abgeschlossen: {new Date(slot.completed_at).toLocaleString()}</div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
            <Layers className="h-6 w-6 mr-3 text-blue-600" />
            Multi-Campaign Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-200">Übersicht und Verwaltung Ihrer Multi-Campaigns</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={() => refetchCampaigns()}
            className="px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-dark-surface flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Aktualisieren</span>
          </button>
        </div>
      </div>

      {/* Statistiken */}
      {campaigns && campaigns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-dark-secondary rounded-lg shadow p-6 border dark:border-dark-copper-dark">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Layers className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Gesamt Campaigns</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-dark-copper-glow">{campaigns.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-secondary rounded-lg shadow p-6 border dark:border-dark-copper-dark">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Aktive Campaigns</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-dark-copper-glow">
                  {campaigns.filter(c => c.status === 'running').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-secondary rounded-lg shadow p-6 border dark:border-dark-copper-dark">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Gesamt E-Mails</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-dark-copper-glow">
                  {campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-secondary rounded-lg shadow p-6 border dark:border-dark-copper-dark">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-200">Erfolgsrate</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-dark-copper-glow">
                  {campaigns.length > 0 
                    ? Math.round(
                        campaigns.reduce((sum, c) => {
                          const total = (c.total_sent || 0) + (c.total_failed || 0);
                          return sum + (total > 0 ? (c.total_sent || 0) / total : 0);
                        }, 0) / campaigns.length * 100
                      )
                    : 0
                  }%
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Liste oder Details */}
      {!selectedCampaign ? (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Campaigns ({campaigns?.length || 0})
          </h2>
          
          {!campaigns || campaigns.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Keine Multi-Campaigns</h3>
              <p className="text-gray-500 dark:text-gray-300">Erstellen Sie Ihre erste Multi-Campaign um zu beginnen.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {campaigns.map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Campaign Details Header */}
          <div className="flex items-center space-x-4 mb-6">
            <button
              onClick={() => setSelectedCampaign(null)}
              className="text-blue-600 hover:text-blue-700 flex items-center space-x-2"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              <span>Zurück zur Übersicht</span>
            </button>
          </div>

          {campaignDetails && (
            <div className="space-y-6">
              {/* Campaign Header */}
              <div className="bg-white dark:bg-dark-secondary rounded-lg shadow p-6 border dark:border-dark-copper-dark">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{campaignDetails.name}</h2>
                    <p className="text-gray-600 dark:text-gray-200">{campaignDetails.description}</p>
                  </div>
                  <StatusBadge status={campaignDetails.status} />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-blue-600">{campaignDetails.total_slots}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Slots</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-green-600">{campaignDetails.total_sent}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Gesendet</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-red-600">{campaignDetails.total_failed}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Fehler</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-purple-600">{campaignDetails.total_recipients}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Empfänger</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-orange-600">
                      {campaignDetails.execution_mode === 'sequential' ? 'Sequenziell' : 'Parallel'}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-300">Modus</div>
                  </div>
                </div>
              </div>

              {/* Slots */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Slots ({campaignDetails.slots?.length || 0})
                </h3>
                
                {campaignDetails.slots && campaignDetails.slots.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {campaignDetails.slots.map((slot, index) => (
                      <SlotDetails 
                        key={slot.id} 
                        slot={slot} 
                        slotNumber={index + 1} 
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-300">
                    <Target className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>Keine Slots gefunden</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiCampaignDashboard;
